import { JobStatus, QuestionSetStatus, RefineResp } from '@/api/client'

export type Phase = 'idle' | 'refining' | 'confirming' | 'compiling' | 'forecasting' | 'done' | 'error'
export type AskMode = 'single' | 'set'

/** Structured source entry in the source-set builder UI. */
export type SourceRow = {
  id: string
  type: 'url' | 'text' | 'note'
  value: string
  label: string
}

let _sourceId = 0
export function nextSourceId(): string {
  return `src_${++_sourceId}_${Date.now()}`
}

/** Infer a reasonable default type from the pasted value. */
export function inferSourceType(value: string): SourceRow['type'] {
  const v = String(value || '').trim()
  if (v.startsWith('http://') || v.startsWith('https://')) return 'url'
  return 'text'
}

export type QueueItem = {
  id: string
  question: string
  status: 'queued' | 'refining' | 'confirming' | 'forecasting' | 'done' | 'failed'
  jobId?: string
  itemId?: string
  prob?: number
  error?: string
  refinement?: RefineResp
  startMs?: number
  createdAt?: string
}

let _queueId = 0
export function nextQueueId() { return `q_${++_queueId}_${Date.now()}` }

/** Base key only; resolve per-org at access time via orgScopedKey(). */
export const ASK_QUEUE_STORAGE_BASE_KEY = 'ask_queue_v1'

export function elapsed(startMs: number): string {
  const sec = Math.max(0, Math.round((Date.now() - startMs) / 1000))
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function queueStatusFromJobStatus(status: string): QueueItem['status'] {
  if (status === 'queued' || status === 'running') return 'forecasting'
  if (status === 'succeeded' || status === 'done') return 'done'
  if (status === 'failed') return 'failed'
  return 'queued'
}

export function queueItemFromJob(job: any): QueueItem | null {
  const question = String(job?.display_question || '').trim()
  if (!question) return null
  return {
    id: `job_${job.job_id}`,
    question,
    status: queueStatusFromJobStatus(String(job.status || 'queued')),
    jobId: String(job.job_id || ''),
    itemId: job.item_id || undefined,
    prob: typeof job?.output_json?.probability === 'number' ? job.output_json.probability : undefined,
    error: job.error || undefined,
    startMs: job.created_at ? Date.parse(job.created_at) : Date.now(),
    createdAt: job.created_at || undefined,
  }
}

export function etaLabel(mode: AskMode, setStatus: QuestionSetStatus | null, startMs: number): string | null {
  if (mode === 'single') {
    const sec = Math.round((Date.now() - startMs) / 1000)
    if (sec < 60) return 'Usually 3-7 minutes'
    if (sec < 180) return 'Still working...'
    if (sec < 420) return 'Almost there...'
    return 'Taking longer than usual...'
  }
  if (!setStatus) return 'Usually 10-30 minutes'
  const gen = setStatus.generated_count || 0
  const done = setStatus.forecasted_count || 0
  const failed = setStatus.failed_count || 0
  const total = gen || 4
  const remaining = Math.max(0, total - done - failed)
  if (remaining <= 0) return null
  const perQ = 300
  const low = Math.ceil((remaining * perQ * 0.6) / 60)
  const high = Math.ceil((remaining * perQ * 1.2) / 60)
  return `~${low}-${high} min remaining`
}

export function stageLabel(
  mode: AskMode,
  phase: Phase,
  jobStatus: JobStatus | null,
  setStatus: QuestionSetStatus | null,
): string {
  if (phase === 'refining') return 'Analyzing question...'
  if (phase === 'compiling') return 'Compiling question...'
  if (phase === 'done') return 'Complete'
  if (mode === 'single') {
    if (jobStatus?.status === 'queued') return 'Queued...'
    return 'Forecasting...'
  }
  const gen = setStatus?.generated_count || 0
  const done = setStatus?.forecasted_count || 0
  const failed = setStatus?.failed_count || 0
  const setStatusStr = (setStatus?.status || '').toLowerCase()
  if (setStatusStr === 'queued' || (!gen && jobStatus?.status === 'queued')) return 'Queued...'
  if (!gen) return 'Ingesting sources and generating questions...'
  const total = gen
  const completed = done + failed
  if (completed < total) return `Forecasting question ${completed + 1} of ${total}...`
  return 'Finishing up...'
}

export function sourceContextForItem(setStatus: QuestionSetStatus | null, item: any): any[] {
  const sources = Array.isArray(setStatus?.sources) ? setStatus.sources : []
  const ids = Array.isArray(item?.source_asset_ids_json) ? item.source_asset_ids_json : []
  if (!sources.length || !ids.length) return []
  const wanted = new Set(ids.map((id: any) => String(id || '').trim()).filter(Boolean))
  return sources.filter((src: any) => wanted.has(String(src?.source_asset_id || '').trim()))
}
